import JsSIP from 'jssip/lib/JsSIP';

import IconBarBad from '@assets/img/icons/bar-bad.svg';
import IconBarGood from '@assets/img/icons/bar-good.svg';
import IconBarNormal from '@assets/img/icons/bar-normal.svg';

import type {
	ConferenceUser,
	ReferHeaderOptions,
	ReferredByIdentity,
	SignalQuality,
	SipTarget,
	SubscriptionTerminatedPayload,
} from '../model/types';
import { isRemoteSendingMedia } from './media';

const SUBSCRIPTION_TERMINATION_CODE = {
	SUBSCRIBE_AUTHENTICATION_FAILED: 4,
	WRONG_NOTIFY_RECEIVED: 7,
} as const;

/**
 * Формирует значение SIP Name-Addr (`"Display Name" <sip:user@host>`).
 *
 * @param identity Целевая SIP-идентичность.
 * @returns Строка для подстановки в SIP-заголовки.
 */
const formatNameAddrValue = ({ aor, displayName }: SipTarget) => {
	if (!displayName) return `<${aor}>`;
	return `"${displayName}" <${aor}>`;
};

/**
 * Универсальный конструктор заголовков REFER.
 *
 * @param options Дополнительные флаги и заголовки.
 */
export const buildReferHeaders = (options: ReferHeaderOptions = {}): string[] => {
	const headers: string[] = [];

	if (options.referToUri) {
		headers.push(`Refer-To: ${formatNameAddrValue(options.referToUri)}`);
	}

	if (options.selfUri) {
		headers.push(`Referred-By: ${formatNameAddrValue(options.selfUri)}`);
	}

	if (options.replaces) {
		headers.push(`Replaces: ${options.replaces}`);
	}

	if (options.contactUri) {
		headers.push(`Contact: ${options.contactUri}`);
	}

	if (options.includeEventRefer) {
		headers.push('Event: refer');
	}

	const requireTokens: string[] = [];
	if (options.requireTdialog) {
		requireTokens.push('tdialog');
	}
	if (options.requireReplaces) {
		requireTokens.push('replaces');
	}
	if (requireTokens.length > 0) {
		headers.push(`Require: ${requireTokens.join(', ')}`);
	}

	if (options.targetDialog) {
		headers.push(`Target-Dialog: ${options.targetDialog}`);
	}

	if (options.includeSupported) {
		headers.push('Supported: replaces');
	}

	if (options.disableReferSubscription) {
		headers.push('Refer-Sub: false', 'Supported: norefersub');
	}

	return headers;
};

/**
 * Возвращает aor и displayName из заголовка
 */
export const getIdentityFromHeader = (
	request: any,
	header: 'Referred-By' | 'Refer-To'
): ReferredByIdentity => {
	const emptyIdentity: ReferredByIdentity = {};
	if (!request) return emptyIdentity;

	const headerValue = request.getHeader?.(header);
	if (!headerValue?.trim()) return emptyIdentity;

	const parsed = JsSIP.NameAddrHeader.parse(headerValue);
	if (!parsed?.uri) {
		return emptyIdentity;
	}

	return {
		aor: parsed.uri.toString?.(),
		displayName: parsed.display_name?.trim?.(),
	};
};

/**
 * Проверяет, отправляет ли участник конференции аудио/видео по данным conference-info.
 *
 * @param participant Пользователь из conference-info.
 * @param mediaType Тип медиа: 'audio' или 'video'.
 * @returns true, если есть хотя бы один endpoint с медиапотоком; иначе false.
 */
export const isParticipantSendingMedia = (
	participant: ConferenceUser,
	mediaType: 'audio' | 'video'
): boolean => {
	for (const endpoint of participant.endpoints ?? []) {
		for (const media of endpoint.media ?? []) {
			if (media.type !== mediaType) continue;
			const status = media.status as RTCRtpTransceiver['currentDirection'];
			if (isRemoteSendingMedia(status)) return true;
		}
	}

	return false;
};

/**
 * Нормализует пару AoR/display-name в объект SipTarget.
 *
 * @param aor SIP AoR целевой стороны.
 * @param displayName Отображаемое имя (если есть).
 * @returns SipTarget с очищенным displayName.
 */
export const makeReferTarget = (aor: string, displayName?: string): SipTarget => ({
	aor,
	displayName: displayName?.trim?.(),
});

/**
 * Приводит имя SIP‑события к каноническому виду:
 * убирает параметры после `;` и делает строку в нижнем регистре.
 *
 * @param name Сырой заголовок Event.
 * @returns Нормализованное имя события.
 */
export const normalizeEventKey = (name: string): string => {
	return name.split(';')[0].trim().toLowerCase();
};

/** Возвращает 0 или положительное число, иначе 0 */
export const safePositive = (v?: number) => (v && v > 0 ? v : 0);

/**
 * Определяет, стоит ли автоматически переподписываться после terminate.
 *
 * @param info Детали завершения подписки.
 * @returns `true`, если retry допустим; иначе `false`.
 */
export const shouldRetrySubscriptionTermination = (
	info: SubscriptionTerminatedPayload
): boolean => {
	const reason = info.reason?.toLowerCase();

	// Финальные причины из NOTIFY — больше не пытаемся, чёрный список финальных причин из NOTIFY (RFC 3265/6665).
	if (reason && ['rejected', 'noresource', 'invariant'].includes(reason)) return false;

	// Аутентификация — бессмысленно крутить без смены кредов.
	if (info.code === SUBSCRIPTION_TERMINATION_CODE.SUBSCRIBE_AUTHENTICATION_FAILED) return false;

	// Плохой NOTIFY (формат/несоответствие) — обычно не ретраим «сразу».
	if (info.code === SUBSCRIPTION_TERMINATION_CODE.WRONG_NOTIFY_RECEIVED) return false;

	// Transport timeout/ошибки — можно пробовать.
	return true;
};

/**
 * Возвращает иконку и человеко-понятную подсказку для указанного качества сигнала.
 *
 * @param signalQuality Уровень качества: 'good' | 'normal' | 'bad'.
 * @returns Объект { signalIcon, signalHint }.
 */
export const signalQualityIcon = (signalQuality: SignalQuality) => {
	let signalIcon = IconBarGood;
	let signalHint = 'Хорошее соединение';

	switch (signalQuality) {
		case 'good': {
			signalIcon = IconBarGood;
			signalHint = 'Хорошее соединение';
			break;
		}
		case 'normal': {
			signalIcon = IconBarNormal;
			signalHint = 'Удовлетворительное соединение';
			break;
		}
		case 'bad': {
			signalIcon = IconBarBad;
			signalHint = 'Плохое соединение';
			break;
		}
		default: {
			break;
		}
	}

	return {
		signalIcon,
		signalHint,
	};
};

/**
 * Валидирует SIP URI (например, "sip:user@host") и, при необходимости,
 * проверяет, что host входит в список разрешённых.
 *
 * @param uri Строка SIP-URI.
 * @param allowedHosts Список разрешённых доменов (необязательно).
 * @returns true, если URI валиден и соответствует ограничениям; иначе false.
 */
export const validateSipLogin = (uri: string, allowedHosts: string[] = []) => {
	try {
		const parsed = JsSIP.URI.parse(uri);

		// 1. Обязательная схема sip:
		if (parsed.scheme !== 'sip') return false;

		// 2. Наличие имени пользователя и хоста:
		if (!parsed.user || !parsed.host) return false;

		// 3. (Необязательно) Ограничиваем список доменов:
		return !(allowedHosts.length > 0 && !allowedHosts.includes(parsed.host));
	} catch (error) {
		return false;
	}
};
